import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { getSource, trackLead } from "../lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = ({ projectType, slug, dark = false }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", description: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        project_type: projectType,
        location: "",
        description: form.description || `Quick estimate request — ${projectType}.`,
        contact_method: "either",
        photo_link: "",
        source: getSource(),
      });
      trackLead();
      setDone(true);
      toast.success("Estimate request received. We'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`lead-success-${slug}`} className={`border p-8 ${dark ? "border-bronze/50 bg-warm/5" : "border-bronze/40 bg-warm2"}`}>
        <CheckCircle2 size={32} className="text-bronze" />
        <h3 className={`h4 mt-4 ${dark ? "text-warm" : "text-charcoal"}`}>Request received.</h3>
        <p className={`mt-2 text-[15px] leading-relaxed ${dark ? "text-warm/65" : "text-mute"}`}>
          Thanks, {form.name.split(" ")[0] || "there"}. We've received your project details and will
          follow up shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`lead-form-${slug}`} className="grid gap-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={`field-label ${dark ? "!text-warm" : ""}`} htmlFor={`${slug}-name`}>Name *</label>
          <input id={`${slug}-name`} data-testid={`lead-name-${slug}`} required className="field" value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div>
          <label className={`field-label ${dark ? "!text-warm" : ""}`} htmlFor={`${slug}-phone`}>Phone *</label>
          <input id={`${slug}-phone`} data-testid={`lead-phone-${slug}`} required type="tel" className="field" value={form.phone} onChange={set("phone")} placeholder="(780) 000-0000" />
        </div>
      </div>
      <div>
        <label className={`field-label ${dark ? "!text-warm" : ""}`} htmlFor={`${slug}-email`}>Email *</label>
        <input id={`${slug}-email`} data-testid={`lead-email-${slug}`} required type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@example.com" />
      </div>
      <div>
        <label className={`field-label ${dark ? "!text-warm" : ""}`} htmlFor={`${slug}-desc`}>Tell us about your project</label>
        <textarea id={`${slug}-desc`} data-testid={`lead-description-${slug}`} rows={3} className="field resize-none" value={form.description} onChange={set("description")} placeholder="Approximate size, timeline, anything that helps." />
      </div>
      <input type="hidden" value={projectType} readOnly />
      <p className={`text-[13px] ${dark ? "text-warm/50" : "text-mute"}`}>
        Project type: <span className="font-semibold">{projectType}</span>
      </p>
      <div>
        <button type="submit" data-testid={`lead-submit-${slug}`} disabled={sending} className="btn btn-bronze group w-full disabled:opacity-60 sm:w-auto">
          {sending ? "Sending..." : "Request a Project Estimate"}
          {!sending && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
        </button>
      </div>
    </form>
  );
};
